// ============================================================================
// stripboardDropDecider — Lane definitions, drop decision logic, and click routing
// for the Stripboard Matrix view.
// ----------------------------------------------------------------------------
// Extracted to keep StripboardMatrixView focused on components for React Refresh.
// ============================================================================

import type { BoardStatus, ChecklistItem, ChecklistWorkflowStatus, TimelineWorkflowStatus } from '../../types/schema'
import type { TimelineElement } from '../../types/timelineElement'
import { isDropAllowedForLane } from '../../services/checklistStatusMachine'

export type StripboardLane = 'UNSCHEDULED' | 'TODO' | 'IN_PROGRESS' | 'ASSESSING' | 'POSTPONED' | 'COMPLETE'
export type StripboardRecordKind = 'ELEMENT' | 'CHECKLIST'
export type StripboardRecord =
  | { source: 'ELEMENT'; id: string; element: TimelineElement }
  | { source: 'CHECKLIST'; id: string; checklist: ChecklistItem }

export interface LaneMeta {
  id: StripboardLane
  label: string
  hint: string
  elementBoardStatus: BoardStatus | null
  allowedKinds: ReadonlyArray<StripboardRecordKind>
  dropAllowed: boolean
}

export const LANES: ReadonlyArray<LaneMeta> = [
  { id: 'UNSCHEDULED', label: 'Unscheduled', hint: 'No date or status', elementBoardStatus: null, allowedKinds: ['ELEMENT', 'CHECKLIST'], dropAllowed: isDropAllowedForLane('UNSCHEDULED') },
  { id: 'TODO', label: 'To Do', hint: 'Ready for planning', elementBoardStatus: 'TODO', allowedKinds: ['ELEMENT', 'CHECKLIST'], dropAllowed: isDropAllowedForLane('TODO') },
  { id: 'IN_PROGRESS', label: 'In Progress', hint: 'Active production', elementBoardStatus: 'IN_PROGRESS', allowedKinds: ['ELEMENT', 'CHECKLIST'], dropAllowed: isDropAllowedForLane('IN_PROGRESS') },
  { id: 'ASSESSING', label: 'Assessing', hint: 'Ready for review', elementBoardStatus: 'IN_PROGRESS', allowedKinds: ['CHECKLIST'], dropAllowed: isDropAllowedForLane('ASSESSING') },
  { id: 'POSTPONED', label: 'Postponed', hint: 'Deferred work', elementBoardStatus: 'TODO', allowedKinds: ['CHECKLIST'], dropAllowed: isDropAllowedForLane('POSTPONED') },
  { id: 'COMPLETE', label: 'Complete', hint: 'Finished or not required', elementBoardStatus: 'COMPLETE', allowedKinds: ['ELEMENT', 'CHECKLIST'], dropAllowed: isDropAllowedForLane('COMPLETE') },
]

export const LANE_BY_ID: Record<StripboardLane, LaneMeta> = LANES.reduce(
  (acc, lane) => { acc[lane.id] = lane; return acc },
  {} as Record<StripboardLane, LaneMeta>,
)

export const CHECKLIST_STATUS_LANE: Record<ChecklistWorkflowStatus, Exclude<StripboardLane, 'UNSCHEDULED'>> = {
  CREATED: 'TODO',
  ASSIGNED: 'TODO',
  ACCEPTED: 'TODO',
  ONGOING: 'IN_PROGRESS',
  ASSESSING: 'ASSESSING',
  POSTPONED: 'POSTPONED',
  COMPLETED: 'COMPLETE',
  NOT_REQUIRED: 'COMPLETE',
}

export const LANE_DEFAULT_CHECKLIST_STATUS: Record<StripboardLane, ChecklistWorkflowStatus | null> = {
  UNSCHEDULED: null,
  TODO: 'CREATED',
  IN_PROGRESS: 'ONGOING',
  ASSESSING: 'ASSESSING',
  POSTPONED: 'POSTPONED',
  COMPLETE: 'COMPLETED',
}

export const CHECKLIST_STATUSES: ChecklistWorkflowStatus[] = [
  'CREATED', 'ASSIGNED', 'ASSESSING', 'ACCEPTED', 'ONGOING', 'COMPLETED', 'NOT_REQUIRED', 'POSTPONED',
]

export const TIMELINE_STATUSES: TimelineWorkflowStatus[] = ['TODO', 'IN_PROGRESS', 'COMPLETE']

export type DropDecision =
  | { action: 'NONE' }
  | { action: 'OPEN_DRAWER'; checklistId: string; pendingStatus: ChecklistWorkflowStatus }
  | { action: 'MOVE_RECORD'; record: StripboardRecord; lane: StripboardLane; checklistWorkflowStatus?: ChecklistWorkflowStatus }

export function decideDropAction(
  record: StripboardRecord | undefined | null,
  laneMeta: LaneMeta | undefined | null,
): DropDecision {
  if (!record || !laneMeta) return { action: 'NONE' }
  if (!laneMeta.dropAllowed) {
    const targetStatus = LANE_DEFAULT_CHECKLIST_STATUS[laneMeta.id]
    if (record.source === 'CHECKLIST' && targetStatus) {
      return { action: 'OPEN_DRAWER', checklistId: record.checklist.id, pendingStatus: targetStatus }
    }
    return { action: 'NONE' }
  }
  return { action: 'MOVE_RECORD', record, lane: laneMeta.id }
}

export type CardClickDecision =
  | { action: 'ELEMENT'; element: TimelineElement }
  | { action: 'CHECKLIST'; checklistId: string; pendingStatus?: ChecklistWorkflowStatus }

export function decideCardClickAction(
  record: StripboardRecord,
  displayedLane?: StripboardLane,
): CardClickDecision {
  if (record.source !== 'CHECKLIST') {
    return { action: 'ELEMENT', element: record.element }
  }
  const currentStatus = record.checklist.workflowStatus
  const lane = displayedLane ?? (currentStatus ? CHECKLIST_STATUS_LANE[currentStatus] : record.checklist.boardStatus ?? 'UNSCHEDULED')
  const laneMeta = LANES.find((l) => l.id === lane)
  const isAlreadyAtTarget =
    currentStatus === (laneMeta ? LANE_DEFAULT_CHECKLIST_STATUS[laneMeta.id] : undefined) ||
    (laneMeta?.id === 'COMPLETE' && currentStatus === 'NOT_REQUIRED')
  const pendingStatus =
    laneMeta && !laneMeta.dropAllowed && !isAlreadyAtTarget
      ? LANE_DEFAULT_CHECKLIST_STATUS[laneMeta.id] ?? undefined
      : undefined
  return { action: 'CHECKLIST', checklistId: record.checklist.id, pendingStatus }
}
